// src/pages/Contact.jsx
import React, { useState } from 'react';
import { saveLead } from '../services/leadService';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Loader2, ArrowRight, Phone, Mail, MapPin } from 'lucide-react';
// --- Contact Configuration ---
const CONTACT_INFO = [
  { 
    icon: Phone, 
    label: "Talk to us", 
    value: "+91 7305959496 / +91 6383703636", 
    desc: "Mon-Sat from 9am to 7pm",
    href: "tel:+917305959496",
    delay: 0.1
  },
  { 
    icon: Mail, 
    label: "Email us", 
    value: "manager.6thsense@gmail.com", 
    desc: "We respond within 2 hours",
    href: "mailto:manager.6thsense@gmail.com",
    delay: 0.2
  },
  { 
    icon: MapPin, 
    label: "Visit us", 
    value: "Trichy, Tamil Nadu", 
    desc: "Come say hello at our HQ",
    href: "#map",
    delay: 0.3
  }
];

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', message: ''
  });
  const [status, setStatus] = useState('idle');

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    
    try {
      await saveLead({
        ...formData,
        source: 'contact_page',
        type: 'contact_inquiry',
        timestamp: new Date().toISOString(),
      });
      setStatus('success');
      setFormData({ name: '', phone: '', email: '', message: '' });
    } catch (error) {
      console.error("Submission error:", error);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans pt-20 lg:pt-28 pb-20">
      

      <div className="max-w-7xl mx-auto px-6">
        
        {/* SECTION 1: HEADER */}
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-20">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl font-serif font-bold text-slate-900 mb-6 mt-5"
          >
            Get in touch with our team
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg text-slate-600 leading-relaxed"
          >
            Whether you have questions about study abroad programs, IELTS training, or just want to say hello, we’re here to help.
          </motion.p>
        </div>

        {/* SECTION 2: CONTACT CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
          {CONTACT_INFO.map((item, idx) => (
            <motion.a
              key={idx}
              href={item.href}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: item.delay }}
              className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group"
            >
              <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center text-red-600 mb-6 group-hover:bg-red-600 group-hover:text-white transition-colors">
                <item.icon size={24} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-1">{item.label}</h3>
              <p className="text-slate-900 font-medium mb-2">{item.value}</p>
              <p className="text-sm text-slate-500">{item.desc}</p>
            </motion.a>
          ))}
        </div>

        {/* SECTION 3: MAP + FORM */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:gap-12 items-start" id="map">
          
          {/* MAP */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="w-full h-[400px] lg:h-[600px] bg-slate-200 rounded-3xl overflow-hidden shadow-lg border border-slate-200 order-2 lg:order-1"
          >
            <iframe
              title="6th Sense Academy Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3918.794652561137!2d78.68356519999999!3d10.827020999999998!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3baaf5e3922b4c09%3A0xf78c07b886771587!2s6th%20Sense%20Training%20Academy!5e0!3m2!1sen!2sin!4v1765468712656!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
            />
          </motion.div>

          {/* FORM */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-white p-8 lg:p-12 rounded-3xl shadow-xl border border-slate-100 order-1 lg:order-2 mb-10 lg:mb-0"
          >
            <div className="mb-8">
              <h2 className="text-3xl font-serif font-bold text-slate-900 mb-2">
                Send a Message
              </h2>
              <p className="text-slate-500">
                Fill out the form below and we will get back to you shortly.
              </p>
            </div>

            <AnimatePresence mode="wait">
              {status === 'success' ? (
                <SuccessMessage />
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <FloatingInput label="Full Name" name="name" value={formData.name} onChange={handleChange} required />
                  <FloatingInput label="Phone Number" name="phone" value={formData.phone} onChange={handleChange} required />
                  <FloatingInput label="Email Address" name="email" type="email" value={formData.email} onChange={handleChange} required />
                  <FloatingInput label="How can we help?" name="message" textarea value={formData.message} onChange={handleChange} required />

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="w-full bg-slate-900 hover:bg-red-600 text-white font-bold text-lg py-4 rounded-xl transition-all duration-300 flex items-center justify-center gap-3"
                  >
                    {status === 'submitting'
                      ? <><Loader2 className="animate-spin" size={20} /> Sending...</>
                      : <><span>Send Message</span> <ArrowRight size={20} /></>}
                  </button>

                  {status === 'error' && (
                    <p className="text-red-500 text-center text-sm bg-red-50 py-2 rounded-lg">
                      Connection error. Please try again.
                    </p>
                  )}
                </form>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

// --- Sub Components ---

const SuccessMessage = () => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className="flex flex-col items-center justify-center min-h-[400px] text-center"
  >
    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
      <CheckCircle2 size={40} className="text-green-600" />
    </div>
    <h3 className="text-2xl font-bold text-slate-900 mb-2">Message Sent!</h3>
    <p className="text-slate-500 max-w-xs mx-auto">
      Thank you for reaching out. Our team will contact you within 2 hours.
    </p>
  </motion.div>
);

const FloatingInput = ({ label, textarea = false, value, ...props }) => {
  const Tag = textarea ? 'textarea' : 'input';
  const isFilled = value && value.length > 0;

  return (
    <div className="relative border-b border-slate-200 pt-6 pb-2 transition-colors focus-within:border-slate-900">
      <Tag
        {...props}
        value={value}
        rows={textarea ? 4 : undefined}
        className="w-full bg-transparent outline-none text-lg font-medium text-slate-900 resize-none py-1"
      />
      <label
        className={`
          absolute left-0 pointer-events-none transition-all duration-300
          ${isFilled
            ? '-top-0 text-xs font-bold uppercase tracking-wide text-slate-500'
            : 'top-6 text-lg text-slate-400'}
          focus-within:-top-0 focus-within:text-xs focus-within:font-bold
          focus-within:uppercase focus-within:tracking-wide focus-within:text-slate-500
        `}
      >
        {label} {props.required && <span className="text-red-500">*</span>}
      </label>
    </div>
  );
};

export default Contact;
